import { useCallback, useState } from "react";
import { useService } from "./useService";

export interface EmailIssue {
  /** A corrected email Loqate suggested, or null when none is offered. */
  suggestion: string | null;
}

/**
 * Non-blocking Loqate email check. Call `checkEmail` on blur once the address
 * is syntactically valid; if Loqate flags it (undeliverable, disposable, or a
 * fraud/complainer risk) an `issue` is raised for the UI to warn on. Any error
 * fails open — the donor is never blocked by this check.
 */
export function useEmailVerification() {
  const service = useService();
  const [issue, setIssue] = useState<EmailIssue | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const checkEmail = useCallback(
    async (email: string): Promise<boolean> => {
      setIsChecking(true);
      try {
        const result = await service.verifyEmail(email);
        const shouldWarn =
          result.status === "invalid" ||
          result.isDisposable ||
          result.isFraudRisk;
        setIssue(shouldWarn ? { suggestion: result.suggestion } : null);
        return shouldWarn;
      } catch {
        setIssue(null);
        return false;
      } finally {
        setIsChecking(false);
      }
    },
    [service],
  );

  const clearIssue = useCallback(() => setIssue(null), []);

  return { issue, isChecking, checkEmail, clearIssue };
}
